import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Camera, FileDown, Loader2, Pencil, Plus, Send, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { SitePhotoCapture } from "@/components/shared/SitePhotoCapture";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useCommitments } from "@/hooks/use-commitments";
import { useTradeCategories } from "@/hooks/use-config";
import {
  type DailyLog,
  type DelayEntry,
  type ManpowerEntry,
  type MaterialDelivery,
  useAddDelayEntry,
  useAddDelivery,
  useAddManpowerEntry,
  useDailyLog,
  useDeleteDelayEntry,
  useDeleteDelivery,
  useDeleteManpowerEntry,
  usePatchDailyLog,
  usePatchDelayEntry,
  usePatchDelivery,
  usePatchManpowerEntry,
  useSubmitDailyLog,
} from "@/hooks/use-daily-logs";
import { useProjectContractors } from "@/hooks/use-project-contractors";
import { useProjects } from "@/hooks/use-projects";
import { useSitePhotos, useUploadSitePhoto } from "@/hooks/use-site-photos";
import { formatDateOnlyLocal } from "@/lib/utils";
import { DailyLogDetailPanels } from "@/routes/dashboard/daily-log-detail-panels";

async function downloadDailyLogPdf(id: string): Promise<void> {
  const res = await fetch(`/api/dailylogs/${encodeURIComponent(id)}/pdf`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `Export failed (${res.status})` }));
    throw new Error((err as { error?: string }).error ?? `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const disposition = res.headers.get("Content-Disposition");
  const match = disposition?.match(/filename="([^"]+)"/);
  a.download = match?.[1] ?? `daily-log-${id.slice(0, 8)}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Tailwind `md` (768px): at this width and up we keep the tabbed layout. */
function useIsMdUp(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mq = window.matchMedia("(min-width: 768px)");
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    },
    () => window.matchMedia("(min-width: 768px)").matches,
    () => true,
  );
}

type WeatherCondition =
  | "clear"
  | "partly_cloudy"
  | "overcast"
  | "rain"
  | "heavy_rain"
  | "snow"
  | "fog"
  | "high_wind"
  | "extreme_heat"
  | "other";

const WEATHER_CONDITIONS: { value: WeatherCondition; label: string }[] = [
  { value: "clear", label: "Clear" },
  { value: "partly_cloudy", label: "Partly Cloudy" },
  { value: "overcast", label: "Overcast" },
  { value: "rain", label: "Rain" },
  { value: "heavy_rain", label: "Heavy Rain" },
  { value: "snow", label: "Snow" },
  { value: "fog", label: "Fog" },
  { value: "high_wind", label: "High Wind" },
  { value: "extreme_heat", label: "Extreme Heat" },
  { value: "other", label: "Other" },
];

const DELAY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "weather", label: "Weather" },
  { value: "material_delivery", label: "Material delivery" },
  { value: "design_change", label: "Design change" },
  { value: "subcontractor", label: "Subcontractor" },
  { value: "owner_decision", label: "Owner decision" },
  { value: "permit", label: "Permit" },
  { value: "unforeseen_conditions", label: "Unforeseen conditions" },
  { value: "other", label: "Other" },
];

type DailyLogStatus = DailyLog["status"];

function StatusBadge({ status }: { status: DailyLogStatus }) {
  const map: Record<
    DailyLogStatus,
    { label: string; variant: "default" | "secondary" | "outline" | "destructive" }
  > = {
    draft: { label: "Draft", variant: "outline" },
    submitted: { label: "Submitted", variant: "default" },
    signed_off: { label: "Signed Off", variant: "default" },
    void: { label: "Void", variant: "secondary" },
  };
  const cfg = map[status];
  return (
    <Badge
      variant={cfg.variant}
      className={
        status === "submitted"
          ? "bg-yellow-100 text-yellow-800 border-yellow-300"
          : status === "signed_off"
            ? "bg-green-100 text-green-800 border-green-300"
            : undefined
      }
    >
      {cfg.label}
    </Badge>
  );
}

function formatDate(date: string | null): string {
  return formatDateOnlyLocal(date, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function parseOptionalInt(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) ? n : null;
}

/** Companies allowed for daily-log manpower: project team and any commitment `companyId` on the project (matches API). */
function useManpowerCompanyOptions(projectId: string) {
  const { data: contractors = [], isLoading: teamLoading } = useProjectContractors(projectId);
  const { data: commitments = [], isLoading: commitmentsLoading } = useCommitments({ projectId });

  const options = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of contractors) {
      const id = c.contractorId;
      const label = c.companyName?.trim() ? c.companyName : "Unnamed company";
      byId.set(id, label);
    }
    for (const cm of commitments) {
      const id = cm.companyId;
      if (!id) continue;
      if (!byId.has(id)) {
        byId.set(id, cm.companyName?.trim() ? cm.companyName : "Unnamed company");
      }
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [contractors, commitments]);

  return {
    options,
    projectContractors: contractors,
    isLoading: teamLoading || commitmentsLoading,
    isEmpty: !teamLoading && !commitmentsLoading && options.length === 0,
  };
}

type ManpowerEntryFormMode = { type: "create" } | { type: "edit"; entry: ManpowerEntry };

interface ManpowerEntryFormProps {
  dailyLogId: string;
  projectId: string;
  mode: ManpowerEntryFormMode;
  onDone: () => void;
}

function ManpowerEntryForm({ dailyLogId, projectId, mode, onDone }: ManpowerEntryFormProps) {
  const [companyId, setCompanyId] = useState("");
  const [tradeCategoryId, setTradeCategoryId] = useState("");
  const [workerCount, setWorkerCount] = useState("");
  const [hoursWorked, setHoursWorked] = useState("");
  const [notes, setNotes] = useState("");

  const editEntry = mode.type === "edit" ? mode.entry : null;

  const {
    options: companyOptions,
    projectContractors,
    isLoading: optionsLoading,
    isEmpty: noCompanies,
  } = useManpowerCompanyOptions(projectId);

  const { data: tradeCategories = [], isLoading: tradesLoading } = useTradeCategories();
  const activeTrades = useMemo(
    () => tradeCategories.filter((t) => t.isActive !== false),
    [tradeCategories],
  );
  const noTradeCategories = !tradesLoading && activeTrades.length === 0;

  /** Avoid `[mode]` / unstable object deps — parent re-renders (queries, photos, etc.) must not reset the form. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: primitives only — `editEntry` identity changes every parent render while editing.
  useEffect(() => {
    if (!editEntry) return;
    const e = editEntry;
    setCompanyId(e.companyId);
    setWorkerCount(String(e.workerCount));
    setHoursWorked(String(e.hoursWorked));
    setNotes(e.notes ?? "");
    const match = activeTrades.find((t) => t.name === e.trade);
    setTradeCategoryId(match?.id ?? "");
  }, [
    editEntry?.id,
    editEntry?.companyId,
    editEntry?.trade,
    editEntry?.workerCount,
    editEntry?.hoursWorked,
    editEntry?.notes,
    activeTrades,
  ]);

  useEffect(() => {
    if (mode.type !== "create") return;
    setCompanyId("");
    setTradeCategoryId("");
    setWorkerCount("");
    setHoursWorked("");
    setNotes("");
  }, [mode.type]);

  useEffect(() => {
    if (!companyId) return;
    const pc = projectContractors.find((c) => c.contractorId === companyId);
    const tcId = pc?.tradeCategoryId;
    if (mode.type === "edit") return;
    if (tcId && activeTrades.some((t) => t.id === tcId)) setTradeCategoryId(tcId);
    else setTradeCategoryId("");
  }, [companyId, projectContractors, activeTrades, mode.type]);

  const addMutation = useAddManpowerEntry(dailyLogId);
  const patchMutation = usePatchManpowerEntry(dailyLogId);
  const savePending = addMutation.isPending || patchMutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!companyId) return;
    const trade = activeTrades.find((t) => t.id === tradeCategoryId)?.name?.trim() ?? "";
    if (!trade) return;
    const notesPayload = notes.trim() === "" ? null : notes;
    if (mode.type === "edit") {
      await patchMutation.mutateAsync({
        entryId: mode.entry.id,
        companyId,
        trade,
        workerCount: Number(workerCount),
        hoursWorked: Number(hoursWorked),
        notes: notesPayload,
      });
      patchMutation.reset();
    } else {
      await addMutation.mutateAsync({
        companyId,
        trade,
        workerCount: Number(workerCount),
        hoursWorked: Number(hoursWorked),
        notes: notesPayload ?? undefined,
      });
      addMutation.reset();
    }
    onDone();
  }

  return (
    <form onSubmit={handleSubmit} className="border rounded-lg p-4 space-y-3 bg-muted/30">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="mp-company">Company / Subcontractor</Label>
          <Select
            value={companyId || undefined}
            onValueChange={setCompanyId}
            disabled={optionsLoading || noCompanies}
          >
            <SelectTrigger id="mp-company" className="min-h-[44px] w-full">
              <SelectValue
                placeholder={
                  optionsLoading
                    ? "Loading companies…"
                    : noCompanies
                      ? "No companies available"
                      : "Select subcontractor"
                }
              />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={4}>
              {companyOptions.map((opt) => (
                <SelectItem key={opt.id} value={opt.id}>
                  {opt.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {noCompanies ? (
            <p className="text-xs text-muted-foreground">
              Add subcontractors to the project team or create a commitment with a company on this
              project to log manpower.
            </p>
          ) : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="mp-trade">Trade</Label>
          <Select
            value={tradeCategoryId || undefined}
            onValueChange={setTradeCategoryId}
            disabled={tradesLoading || noTradeCategories}
          >
            <SelectTrigger id="mp-trade" className="min-h-[44px] w-full">
              <SelectValue
                placeholder={
                  tradesLoading
                    ? "Loading trades…"
                    : noTradeCategories
                      ? "No trade categories"
                      : "Select trade"
                }
              />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={4}>
              {activeTrades.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                  {t.csiDivision ? ` (${t.csiDivision})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {noTradeCategories ? (
            <p className="text-xs text-muted-foreground">
              Add trade categories under Admin → Config → Trade categories.
            </p>
          ) : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="mp-workers">Worker Count</Label>
          <Input
            id="mp-workers"
            type="number"
            min="0"
            value={workerCount}
            onChange={(e) => setWorkerCount(e.target.value)}
            required
            className="min-h-[44px]"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="mp-hours">Hours Worked</Label>
          <Input
            id="mp-hours"
            type="number"
            min="0"
            step="0.5"
            value={hoursWorked}
            onChange={(e) => setHoursWorked(e.target.value)}
            required
            className="min-h-[44px]"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="mp-notes">Notes (optional)</Label>
          <Input
            id="mp-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes"
            className="min-h-[44px]"
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" onClick={onDone} disabled={savePending}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={
            savePending || !companyId || noCompanies || !tradeCategoryId || noTradeCategories
          }
          className="min-h-[44px]"
        >
          {savePending ? "Saving…" : mode.type === "edit" ? "Save changes" : "Add Entry"}
        </Button>
      </div>
    </form>
  );
}

type DeliveryEntryFormMode = { type: "create" } | { type: "edit"; delivery: MaterialDelivery };

function deliveryTimeForInput(t: string | null): string {
  if (!t) return "";
  const s = String(t).trim();
  return s.length >= 5 ? s.slice(0, 5) : s;
}

interface DeliveryEntryFormProps {
  dailyLogId: string;
  mode: DeliveryEntryFormMode;
  onDone: () => void;
}

function DeliveryEntryForm({ dailyLogId, mode, onDone }: DeliveryEntryFormProps) {
  const [materialDescription, setMaterialDescription] = useState("");
  const [quantity, setQuantity] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [receivedBy, setReceivedBy] = useState("");
  const [notes, setNotes] = useState("");

  const editDelivery = mode.type === "edit" ? mode.delivery : null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: primitives only — `editDelivery` identity changes every parent render while editing.
  useEffect(() => {
    if (!editDelivery) return;
    const d = editDelivery;
    setMaterialDescription(d.materialDescription);
    setQuantity(d.quantity);
    setDeliveryTime(deliveryTimeForInput(d.deliveryTime));
    setReceivedBy(d.receivedBy ?? "");
    setNotes(d.notes ?? "");
  }, [
    editDelivery?.id,
    editDelivery?.materialDescription,
    editDelivery?.quantity,
    editDelivery?.deliveryTime,
    editDelivery?.receivedBy,
    editDelivery?.notes,
  ]);

  useEffect(() => {
    if (mode.type !== "create") return;
    setMaterialDescription("");
    setQuantity("");
    setDeliveryTime("");
    setReceivedBy("");
    setNotes("");
  }, [mode.type]);

  const addMutation = useAddDelivery(dailyLogId);
  const patchMutation = usePatchDelivery(dailyLogId);
  const savePending = addMutation.isPending || patchMutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mode.type === "edit") {
      await patchMutation.mutateAsync({
        entryId: mode.delivery.id,
        materialDescription,
        quantity,
        deliveryTime: deliveryTime.trim() === "" ? null : deliveryTime,
        receivedBy: receivedBy.trim() === "" ? null : receivedBy,
        notes: notes.trim() === "" ? null : notes,
      });
      patchMutation.reset();
    } else {
      await addMutation.mutateAsync({
        materialDescription,
        quantity,
        deliveryTime: deliveryTime || undefined,
        receivedBy: receivedBy || undefined,
        notes: notes || undefined,
      });
      addMutation.reset();
    }
    onDone();
  }

  return (
    <form onSubmit={handleSubmit} className="border rounded-lg p-4 space-y-3 bg-muted/30">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="del-desc">Material Description</Label>
          <Input
            id="del-desc"
            value={materialDescription}
            onChange={(e) => setMaterialDescription(e.target.value)}
            placeholder="e.g. Rebar #5, 2x4 studs"
            required
            className="min-h-[44px]"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="del-qty">Quantity</Label>
          <Input
            id="del-qty"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="e.g. 500 lbs, 3 pallets"
            required
            className="min-h-[44px]"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="del-time">Time Received</Label>
          <Input
            id="del-time"
            type="time"
            value={deliveryTime}
            onChange={(e) => setDeliveryTime(e.target.value)}
            className="min-h-[44px]"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="del-recv">Received By</Label>
          <Input
            id="del-recv"
            value={receivedBy}
            onChange={(e) => setReceivedBy(e.target.value)}
            placeholder="Name of person who received"
            className="min-h-[44px]"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="del-notes">Notes (optional)</Label>
          <Input
            id="del-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="min-h-[44px]"
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" onClick={onDone} disabled={savePending}>
          Cancel
        </Button>
        <Button type="submit" disabled={savePending} className="min-h-[44px]">
          {savePending ? "Saving…" : mode.type === "edit" ? "Save changes" : "Log Delivery"}
        </Button>
      </div>
    </form>
  );
}

function ManpowerEntryRow({
  entry,
  isReadOnly,
  onEdit,
  onDelete,
}: {
  entry: ManpowerEntry;
  isReadOnly: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-3 border-b last:border-0">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm">{entry.companyName}</p>
        <p className="text-xs text-muted-foreground">{entry.trade}</p>
        <p className="text-sm font-medium mt-1">
          {entry.workerCount} workers · {entry.hoursWorked} hrs
        </p>
        {entry.notes ? (
          <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{entry.notes}</p>
        ) : null}
      </div>
      {!isReadOnly ? (
        <div className="flex shrink-0 gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={onEdit}
            aria-label="Edit manpower entry"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-destructive hover:text-destructive"
            onClick={onDelete}
            aria-label="Delete manpower entry"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function DeliveryRow({
  delivery,
  isReadOnly,
  onEdit,
  onDelete,
}: {
  delivery: MaterialDelivery;
  isReadOnly: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const timeLabel = deliveryTimeForInput(delivery.deliveryTime);
  return (
    <div className="flex items-start justify-between py-3 border-b last:border-0 gap-4">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{delivery.materialDescription}</p>
        <p className="text-xs text-muted-foreground">Qty: {delivery.quantity}</p>
        {delivery.receivedBy ? (
          <p className="text-xs text-muted-foreground">Received by: {delivery.receivedBy}</p>
        ) : null}
        {delivery.notes ? (
          <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{delivery.notes}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-start gap-2">
        {timeLabel ? (
          <span className="text-xs text-muted-foreground whitespace-nowrap pt-0.5">
            {timeLabel}
          </span>
        ) : null}
        {!isReadOnly ? (
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={onEdit}
              aria-label="Edit delivery entry"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-destructive hover:text-destructive"
              onClick={onDelete}
              aria-label="Delete delivery entry"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function delayTypeLabel(value: string): string {
  return DELAY_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

function DelayRow({
  delay,
  isReadOnly,
  onEdit,
  onDelete,
}: {
  delay: DelayEntry;
  isReadOnly: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-start justify-between py-3 border-b last:border-0 gap-4">
      <div className="flex-1 min-w-0 space-y-1">
        <p className="font-medium text-sm">{delayTypeLabel(delay.delayType)}</p>
        <p className="text-sm">{delay.description}</p>
        {delay.isCompensable ? (
          <Badge variant="outline" className="w-fit text-xs">
            Compensable
          </Badge>
        ) : null}
      </div>
      <div className="flex shrink-0 items-start gap-2">
        {delay.hoursLost != null ? (
          <span className="text-xs text-muted-foreground whitespace-nowrap pt-0.5">
            {delay.hoursLost} hrs lost
          </span>
        ) : null}
        {!isReadOnly ? (
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={onEdit}
              aria-label="Edit delay entry"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-destructive hover:text-destructive"
              onClick={onDelete}
              aria-label="Delete delay entry"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

type DelayEntryFormMode = { type: "create" } | { type: "edit"; delay: DelayEntry };

interface DelayEntryFormProps {
  dailyLogId: string;
  mode: DelayEntryFormMode;
  onDone: () => void;
}

function DelayEntryForm({ dailyLogId, mode, onDone }: DelayEntryFormProps) {
  const [delayType, setDelayType] = useState("weather");
  const [description, setDescription] = useState("");
  const [hoursLost, setHoursLost] = useState("0");
  const [isCompensable, setIsCompensable] = useState(false);

  const editDelay = mode.type === "edit" ? mode.delay : null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: primitives only — `editDelay` identity changes every parent render while editing.
  useEffect(() => {
    if (!editDelay) return;
    setDelayType(editDelay.delayType);
    setDescription(editDelay.description);
    setHoursLost(editDelay.hoursLost != null ? String(editDelay.hoursLost) : "0");
    setIsCompensable(editDelay.isCompensable);
  }, [
    editDelay?.id,
    editDelay?.delayType,
    editDelay?.description,
    editDelay?.hoursLost,
    editDelay?.isCompensable,
  ]);

  useEffect(() => {
    if (mode.type !== "create") return;
    setDelayType("weather");
    setDescription("");
    setHoursLost("0");
    setIsCompensable(false);
  }, [mode.type]);

  const addMutation = useAddDelayEntry(dailyLogId);
  const patchMutation = usePatchDelayEntry(dailyLogId);
  const savePending = addMutation.isPending || patchMutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const hrs = Number.parseFloat(hoursLost);
    const hoursLostNum = Number.isFinite(hrs) ? hrs : 0;
    if (mode.type === "edit") {
      await patchMutation.mutateAsync({
        entryId: mode.delay.id,
        delayType,
        description,
        hoursLost: hoursLostNum,
        isCompensable,
      });
      patchMutation.reset();
    } else {
      await addMutation.mutateAsync({
        delayType,
        description,
        hoursLost: hoursLostNum,
        isCompensable,
      });
    }
    onDone();
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="border rounded-lg p-4 space-y-3 bg-muted/30"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Delay type</Label>
          <Select value={delayType} onValueChange={setDelayType}>
            <SelectTrigger className="min-h-[44px] w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={4}>
              {DELAY_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="delay-hours">Hours lost</Label>
          <Input
            id="delay-hours"
            type="number"
            min="0"
            step="0.5"
            value={hoursLost}
            onChange={(e) => setHoursLost(e.target.value)}
            className="min-h-[44px]"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="delay-desc">Description</Label>
          <Textarea
            id="delay-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What was delayed and impact…"
            required
            rows={3}
            className="min-h-[88px]"
          />
        </div>
        <div className="flex items-center gap-2 sm:col-span-2">
          <Checkbox
            id="delay-compensable"
            checked={isCompensable}
            onCheckedChange={(v) => setIsCompensable(v === true)}
          />
          <Label htmlFor="delay-compensable" className="font-normal cursor-pointer">
            Compensable delay
          </Label>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" onClick={onDone} disabled={savePending}>
          Cancel
        </Button>
        <Button type="submit" disabled={savePending} className="min-h-[44px]">
          {savePending ? "Saving…" : mode.type === "edit" ? "Save changes" : "Log Delay"}
        </Button>
      </div>
    </form>
  );
}

export function DailyLogDetailPage() {
  const isMdUp = useIsMdUp();
  const rawParams = useParams({ strict: false });
  const id = typeof rawParams.id === "string" ? rawParams.id : "";
  const { data: log, isLoading, isError, error, refetch } = useDailyLog(id, id.length > 0);
  const patchDailyLog = usePatchDailyLog(id);
  const submitMutation = useSubmitDailyLog();
  const [pdfBusy, setPdfBusy] = useState(false);
  const deleteManpowerMutation = useDeleteManpowerEntry(id);
  const deleteDeliveryMutation = useDeleteDelivery(id);
  const deleteDelayMutation = useDeleteDelayEntry(id);
  const uploadPhoto = useUploadSitePhoto();
  const { data: projects = [] } = useProjects();

  const projectName = log?.projectId
    ? projects.find((p) => p.id === log.projectId)?.name
    : undefined;

  const [weatherCondition, setWeatherCondition] = useState("");
  const [tempHigh, setTempHigh] = useState("");
  const [tempLow, setTempLow] = useState("");

  const { data: dailyPhotos = [], isLoading: photosLoading } = useSitePhotos(
    log ? { projectId: log.projectId, dailyLogId: log.id } : undefined,
  );

  const [showManpowerForm, setShowManpowerForm] = useState(false);
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [editingManpowerId, setEditingManpowerId] = useState<string | null>(null);
  const [editingDeliveryId, setEditingDeliveryId] = useState<string | null>(null);
  const [deleteManpowerId, setDeleteManpowerId] = useState<string | null>(null);
  const [deleteDeliveryId, setDeleteDeliveryId] = useState<string | null>(null);
  const [deleteDelayId, setDeleteDelayId] = useState<string | null>(null);
  const [showDelayForm, setShowDelayForm] = useState(false);
  const [editingDelayId, setEditingDelayId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");

  // biome-ignore lint/correctness/useExhaustiveDependencies: `id` resets notes when switching logs that share identical note text; `log?.notes` syncs server updates.
  useEffect(() => {
    setNotes(log?.notes ?? "");
  }, [id, log?.notes]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync local weather when route log id or persisted weather fields change (avoid depending on whole `log` object).
  useEffect(() => {
    if (!log) return;
    setWeatherCondition(log.weatherCondition ?? "");
    setTempHigh(log.weatherTempHigh != null ? String(log.weatherTempHigh) : "");
    setTempLow(log.weatherTempLow != null ? String(log.weatherTempLow) : "");
  }, [log?.id, log?.weatherCondition, log?.weatherTempHigh, log?.weatherTempLow]);

  const weatherDirty =
    log != null &&
    (weatherCondition !== (log.weatherCondition ?? "") ||
      parseOptionalInt(tempHigh) !== log.weatherTempHigh ||
      parseOptionalInt(tempLow) !== log.weatherTempLow);

  const isReadOnly =
    log?.status === "submitted" || log?.status === "signed_off" || log?.status === "void";

  if (!id) {
    return <ErrorState message="Missing daily log ID. Open this page from the daily logs list." />;
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !log) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load daily log"}
        onRetry={() => void refetch()}
      />
    );
  }

  const totalWorkers = log.manpowerEntries.reduce((sum, e) => sum + e.workerCount, 0);
  const totalHours = log.manpowerEntries.reduce((sum, e) => sum + e.hoursWorked, 0);
  const editingManpowerEntry =
    editingManpowerId != null
      ? log.manpowerEntries.find((e) => e.id === editingManpowerId)
      : undefined;
  const editingDeliveryRecord =
    editingDeliveryId != null ? log.deliveries.find((d) => d.id === editingDeliveryId) : undefined;
  const editingDelayRecord =
    editingDelayId != null ? log.delays.find((d) => d.id === editingDelayId) : undefined;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/daily-logs"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Daily Logs
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">{formatDate(log.date)}</h1>
          {projectName ? <p className="mt-1 text-muted-foreground text-sm">{projectName}</p> : null}
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={log.status} />
            {log.submittedByName && (
              <span className="text-xs text-muted-foreground">
                Submitted by {log.submittedByName}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            className="min-h-[44px]"
            disabled={pdfBusy}
            onClick={() => {
              setPdfBusy(true);
              void downloadDailyLogPdf(id)
                .catch((e: unknown) => {
                  toast.error(e instanceof Error ? e.message : "Export failed");
                })
                .finally(() => setPdfBusy(false));
            }}
          >
            {pdfBusy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="mr-2 h-4 w-4" />
            )}
            Export PDF
          </Button>
          {log.status === "draft" ? (
            <Button
              type="button"
              className="min-h-[44px]"
              disabled={submitMutation.isPending}
              onClick={() => submitMutation.mutate(id)}
            >
              <Send className="mr-2 h-4 w-4" />
              {submitMutation.isPending ? "Submitting…" : "Submit"}
            </Button>
          ) : null}
        </div>
      </div>

      <DailyLogDetailPanels
        isMdUp={isMdUp}
        manpowerCount={totalWorkers}
        deliveriesCount={log.deliveries.length}
        delaysCount={log.delays.length}
        photosCount={dailyPhotos.length}
        renderOverview={() => (
          <Card>
            <CardContent className="py-4 px-4 space-y-3">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="text-sm font-medium">{formatDate(log.date)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <div className="mt-0.5">
                    <StatusBadge status={log.status} />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Project</p>
                  <p className="text-sm font-medium">{projectName ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Created By</p>
                  <p className="text-sm font-medium">{log.createdBy ?? "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Delay entries</p>
                  <p className="text-sm font-medium">{log.delays.length}</p>
                </div>
                {log.submittedAt && (
                  <div>
                    <p className="text-xs text-muted-foreground">Submitted At</p>
                    <p className="text-sm font-medium">
                      {new Date(log.submittedAt).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
        renderWeather={() => (
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-base">Weather</CardTitle>
                {!isReadOnly && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-[40px] shrink-0"
                    disabled={!weatherDirty || patchDailyLog.isPending}
                    onClick={() =>
                      void patchDailyLog.mutateAsync({
                        weatherCondition: weatherCondition.trim() === "" ? null : weatherCondition,
                        weatherTempHigh: parseOptionalInt(tempHigh),
                        weatherTempLow: parseOptionalInt(tempLow),
                      })
                    }
                  >
                    {patchDailyLog.isPending ? "Saving…" : "Save weather"}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                One condition per log matches the current database field. Afternoon-specific weather
                can go in Notes until the data model expands.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="weather-condition">Conditions</Label>
                  <Select
                    value={weatherCondition || undefined}
                    onValueChange={setWeatherCondition}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger id="weather-condition" className="min-h-[44px] w-full">
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      {WEATHER_CONDITIONS.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="temp-high">Temp High (°F)</Label>
                  <Input
                    id="temp-high"
                    type="number"
                    value={tempHigh}
                    onChange={(e) => setTempHigh(e.target.value)}
                    disabled={isReadOnly}
                    className="min-h-[44px]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="temp-low">Temp Low (°F)</Label>
                  <Input
                    id="temp-low"
                    type="number"
                    value={tempLow}
                    onChange={(e) => setTempLow(e.target.value)}
                    disabled={isReadOnly}
                    className="min-h-[44px]"
                  />
                </div>
              </div>

              <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                {log.hasWeatherDelay ? (
                  <span>
                    At least one <strong className="text-foreground">weather</strong> delay entry
                    exists for this log.
                  </span>
                ) : (
                  <span>No weather-type delay entries on this log.</span>
                )}
              </div>
            </CardContent>
          </Card>
        )}
        renderManpower={() => (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Manpower</CardTitle>
                {!isReadOnly && !showManpowerForm && !editingManpowerId && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[36px] gap-1"
                    onClick={() => {
                      setEditingManpowerId(null);
                      setShowManpowerForm(true);
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Entry
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {showManpowerForm && (
                <ManpowerEntryForm
                  key="manpower-create"
                  dailyLogId={log.id}
                  projectId={log.projectId}
                  mode={{ type: "create" }}
                  onDone={() => setShowManpowerForm(false)}
                />
              )}
              {editingManpowerEntry && (
                <ManpowerEntryForm
                  key={editingManpowerEntry.id}
                  dailyLogId={log.id}
                  projectId={log.projectId}
                  mode={{ type: "edit", entry: editingManpowerEntry }}
                  onDone={() => setEditingManpowerId(null)}
                />
              )}

              {log.manpowerEntries.length === 0 && !showManpowerForm && !editingManpowerId && (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No manpower entries yet.
                </p>
              )}

              {log.manpowerEntries
                .filter((e) => e.id !== editingManpowerId)
                .map((entry) => (
                  <ManpowerEntryRow
                    key={entry.id}
                    entry={entry}
                    isReadOnly={isReadOnly}
                    onEdit={() => {
                      setShowManpowerForm(false);
                      setEditingManpowerId(entry.id);
                    }}
                    onDelete={() => setDeleteManpowerId(entry.id)}
                  />
                ))}

              {log.manpowerEntries.length > 0 && (
                <div className="flex justify-end gap-6 pt-2 border-t text-sm font-medium">
                  <span>Total: {totalWorkers} workers</span>
                  <span>{totalHours} hrs</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        renderDeliveries={() => (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Deliveries</CardTitle>
                {!isReadOnly && !showDeliveryForm && !editingDeliveryId && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[36px] gap-1"
                    onClick={() => {
                      setEditingDeliveryId(null);
                      setShowDeliveryForm(true);
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Delivery
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {showDeliveryForm && (
                <DeliveryEntryForm
                  key="delivery-create"
                  dailyLogId={log.id}
                  mode={{ type: "create" }}
                  onDone={() => setShowDeliveryForm(false)}
                />
              )}
              {editingDeliveryRecord && (
                <DeliveryEntryForm
                  key={editingDeliveryRecord.id}
                  dailyLogId={log.id}
                  mode={{ type: "edit", delivery: editingDeliveryRecord }}
                  onDone={() => setEditingDeliveryId(null)}
                />
              )}

              {log.deliveries.length === 0 && !showDeliveryForm && !editingDeliveryId && (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No deliveries logged yet.
                </p>
              )}

              {log.deliveries
                .filter((d) => d.id !== editingDeliveryId)
                .map((delivery) => (
                  <DeliveryRow
                    key={delivery.id}
                    delivery={delivery}
                    isReadOnly={isReadOnly}
                    onEdit={() => {
                      setShowDeliveryForm(false);
                      setEditingDeliveryId(delivery.id);
                    }}
                    onDelete={() => setDeleteDeliveryId(delivery.id)}
                  />
                ))}
            </CardContent>
          </Card>
        )}
        renderDelays={() => (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Delays</CardTitle>
                {!isReadOnly && !showDelayForm && !editingDelayId && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[36px] gap-1"
                    onClick={() => {
                      setEditingDelayId(null);
                      setShowDelayForm(true);
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Log Delay
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {showDelayForm && (
                <DelayEntryForm
                  key="delay-create"
                  dailyLogId={log.id}
                  mode={{ type: "create" }}
                  onDone={() => setShowDelayForm(false)}
                />
              )}
              {editingDelayRecord && (
                <DelayEntryForm
                  key={editingDelayRecord.id}
                  dailyLogId={log.id}
                  mode={{ type: "edit", delay: editingDelayRecord }}
                  onDone={() => setEditingDelayId(null)}
                />
              )}

              {log.delays.length === 0 && !showDelayForm && !editingDelayId && (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No delays recorded yet.
                </p>
              )}

              {log.delays
                .filter((d) => d.id !== editingDelayId)
                .map((delay) => (
                  <DelayRow
                    key={delay.id}
                    delay={delay}
                    isReadOnly={isReadOnly}
                    onEdit={() => {
                      setShowDelayForm(false);
                      setEditingDelayId(delay.id);
                    }}
                    onDelete={() => setDeleteDelayId(delay.id)}
                  />
                ))}
            </CardContent>
          </Card>
        )}
        renderPhotos={() => (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-base">Photos</CardTitle>
                {!isReadOnly && (
                  <SitePhotoCapture
                    disabled={uploadPhoto.isPending}
                    onCapture={(file, location) => {
                      void uploadPhoto.mutateAsync({
                        file,
                        projectId: log.projectId,
                        dailyLogId: log.id,
                        lat: location?.lat,
                        lng: location?.lng,
                      });
                    }}
                    onMultiCapture={(files, location) => {
                      void Promise.all(
                        files.map((file) =>
                          uploadPhoto.mutateAsync({
                            file,
                            projectId: log.projectId,
                            dailyLogId: log.id,
                            lat: location?.lat,
                            lng: location?.lng,
                          }),
                        ),
                      );
                    }}
                    multiple
                    label={uploadPhoto.isPending ? "Uploading…" : "Add Photo"}
                  />
                )}
              </div>
            </CardHeader>
            <CardContent>
              {photosLoading ? (
                <p className="text-sm text-muted-foreground py-8 text-center">Loading photos…</p>
              ) : dailyPhotos.length === 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-3 flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Camera className="h-8 w-8 mb-2 opacity-40" />
                    <p className="text-sm text-center max-w-sm">
                      No photos for this daily log yet. Use Add Photo to upload from your device.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {dailyPhotos.map((photo) => (
                    <a
                      key={photo.id}
                      href={photo.fullUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="aspect-square rounded-lg overflow-hidden border bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <img
                        src={photo.thumbnailUrl}
                        alt={photo.caption ?? "Site photo"}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        renderNotes={() => (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">General Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isReadOnly}
                placeholder="Enter general notes for this daily log…"
                className="min-h-[160px]"
              />
              {!isReadOnly && (
                <div className="flex justify-end mt-3">
                  <Button
                    className="min-h-[44px]"
                    disabled={patchDailyLog.isPending || notes === (log.notes ?? "")}
                    onClick={() =>
                      void patchDailyLog.mutateAsync({
                        notes: notes.trim() === "" ? null : notes.trim(),
                      })
                    }
                  >
                    {patchDailyLog.isPending ? "Saving…" : "Save Notes"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      />

      <ConfirmDialog
        open={deleteManpowerId != null}
        onOpenChange={(open) => {
          if (!open) setDeleteManpowerId(null);
        }}
        title="Remove manpower entry?"
        description="This removes this manpower row from the daily log."
        variant="destructive"
        confirmLabel="Remove"
        loading={deleteManpowerMutation.isPending}
        onConfirm={async () => {
          const target = deleteManpowerId;
          if (!target) return;
          await deleteManpowerMutation.mutateAsync(target);
          setEditingManpowerId((cur) => (cur === target ? null : cur));
          setDeleteManpowerId(null);
        }}
      />
      <ConfirmDialog
        open={deleteDeliveryId != null}
        onOpenChange={(open) => {
          if (!open) setDeleteDeliveryId(null);
        }}
        title="Remove delivery entry?"
        description="This removes this delivery row from the daily log."
        variant="destructive"
        confirmLabel="Remove"
        loading={deleteDeliveryMutation.isPending}
        onConfirm={async () => {
          const target = deleteDeliveryId;
          if (!target) return;
          await deleteDeliveryMutation.mutateAsync(target);
          setEditingDeliveryId((cur) => (cur === target ? null : cur));
          setDeleteDeliveryId(null);
        }}
      />
      <ConfirmDialog
        open={deleteDelayId != null}
        onOpenChange={(open) => {
          if (!open) setDeleteDelayId(null);
        }}
        title="Remove delay entry?"
        description="This removes this delay row from the daily log."
        variant="destructive"
        confirmLabel="Remove"
        loading={deleteDelayMutation.isPending}
        onConfirm={async () => {
          const target = deleteDelayId;
          if (!target) return;
          await deleteDelayMutation.mutateAsync(target);
          setEditingDelayId((cur) => (cur === target ? null : cur));
          setDeleteDelayId(null);
        }}
      />
    </div>
  );
}
